<template>
  <v-form
    ref="form"
    :disabled="disabled"
  >
    <!-- Top edit button -->
    <avs-editInfoButton
      v-if="isEditable"
      :isEnabled="!disabled"
      @click="$event => setEditable(disabled)"
    />

    <CompanyOwnedInput
      v-model="this.$store.getters['app/getCompany']"
      :rules="rules.company"
      outlined
      disabled
    />

    <v-text-field
      v-model="template.name"
      :rules="rules.name"
      :hint="$t('views.template.hint.name')"
      :label="$t('commons.name')"
      counter="64"
      maxlength="64"
      dense
      outlined
    ></v-text-field>

    <v-text-field
      v-model="template.title"
      :rules="rules.title"
      :hint="$t('views.template.hint.title')"
      :label="$t('commons.title')"
      counter="64"
      maxlength="64"
      dense
      outlined
    ></v-text-field>

    <!-- <v-textarea
      v-model="template.description"
      :rules="rules.description"
      :hint="$t('views.template.hint.description')"
      :label="$t('commons.description')"
      dense
      outlined
    ></v-textarea> -->

    <div style="margin-bottom: 25px">
      <label>{{ $t("commons.description") }}</label>
      <ckeditor
        :editor="editor"
        tag-name="textarea"
        rows="5"
        v-model="template.description"
        :config="editorConfig"
        :hint="$t('views.template.hint.description')"
        :label="$t('commons.description')"
        :disabled="disabled"
        className="content-element"
      ></ckeditor>
    </div>
    <v-select
      v-model="template.type"
      :items="templateTypes"
      :rules="rules.type"
      item-text="name"
      item-value="id"
      :hint="$t('views.template.hint.type')"
      :label="$t('commons.type')"
      dense
      outlined
      persistent-hint
      return-object
      single-line
    ></v-select>

    <v-text-field
      v-model="template.nb_signatures"
      dense
      :hint="$t('views.template.hint.signature')"
      :label="$t('views.template.nb_signatures')"
      outlined
      persistent-hint
      type="number"
      @keypress="isNumber($event)"
    >
    </v-text-field>

    <div v-if="isEditable && !disabled">
      <v-col class="d-flex justify-end gap-4">
        <avs-saveButton @click="handleSave" />
        <avs-cancelButton @click="handleCancel" />
      </v-col>
    </div>
  </v-form>
</template>

<script>
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import { CompanyOwnedInput } from "@/components"
import { TemplateService } from "@/services/documents"

// import { required, slug, max64 } from "@/utils/validator"
import { required, max64 } from "@/utils/validator"
import { isEqual } from "@/utils/object"
import { isNumber } from "@/utils/string"

export default {
  name: "TemplateForm",
  components: { CompanyOwnedInput },
  props: {
    value: {
      type: Object,
      required: true
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    rulesUpdate: {
      type: Object,
      default: () => ({})
    }
  },
  created() {
    if (this.isEditable) {
      this.fetchTemplateTypes()
    }
  },
  computed: {
    rules() {
      return {
        company: [required],
        description: [],
        // name: [required, slug, max64],
        name: [required, max64],
        title: [required, max64],
        type: [required],
        ...this.rulesUpdate
      }
    }
  },
  data() {
    return {
      disabled: true,
      template: null,
      templateTypes: [],
      editor: ClassicEditor,
      editorConfig: {
        toolbar: [
          "paragraph",
          "heading1",
          "heading2",
          "bold",
          "italic",
          "|",
          "link",
          "|",
          "undo",
          "redo",
          "-",
          "numberedList",
          "bulletedList"
        ]
      }
    }
  },
  methods: {
    isNumber,
    fetchTemplateTypes() {
      TemplateService.types.getAll().then(response => {
        this.templateTypes = response
      })
    },
    handleCancel() {
      this.setEditable(false)
      this.setTemplate(this.value)
      this.$emit("cancel")
    },
    async handleSave() {
      if (this.$refs.form.validate()) {
        if (isEqual(this.template, this.value)) {
          // Objects are equal, no changes to perform
          this.$emit("success", false)
          this.setEditable(false)
        } else {
          this.template.nb_signatures = !this.template.nb_signatures
            ? 0
            : this.template.nb_signatures
          this.performSave(this.template)
            .then(response => {
              this.$emit("input", response)
              this.$emit("success", true)
              this.setEditable(false)
            })
            .catch(error => {
              const message = error?.response.data
              this.$emit("error", message)
            })
        }
      }
    },
    async performSave(data) {
      if (data.id > 0) {
        return TemplateService.put(data)
      } else {
        data.company = this.$store.getters["app/getCompany"]
        return TemplateService.post(data)
      }
    },
    setEditable(value) {
      this.disabled = !value
    },
    setTemplate(newValue) {
      if (newValue.company === null) {
        newValue.company = this.$store.state.app.user.companies[0] || null
      }
      this.template = Object.assign({}, newValue)
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue) {
          this.setTemplate(newValue)
        }
      }
    }
  }
}
</script>
<style>
.ck.ck-editor__main > .ck-editor__editable {
  height: 200px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}

.ck.ck-editor__main > .ck-editor__editable {
  height: 130px;
}
.ck.ck-balloon-panel.ck-powered-by-balloon .ck.ck-powered-by {
  display: none !important;
}
.ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
.ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
}

.ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar,
.ck.ck-editor__top .ck-sticky-panel .ck-toolbar.ck-rounded-corners {
  border-radius: var(--ck-border-radius);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.ck-editor__editable {
  color: rgba(0, 0, 0, 0.87) !important;
}
</style>
